import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Shield, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  { key: "has_valid_passport", label: "Do you have a valid passport (6+ months validity)?" },
  { key: "has_financial_proof", label: "Can you provide bank statements showing sufficient funds?" },
  { key: "has_travel_history", label: "Do you have previous international travel history?" },
];

export default function EligibilityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dest = searchParams.get("destination") || "";
  const nat = searchParams.get("nationality") || "";
  const vType = searchParams.get("type") || "tourist";
  const proc = searchParams.get("processing") || "standard";
  const [answers, setAnswers] = useState({ has_valid_passport: false, has_financial_proof: false, has_travel_history: false });
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnswer = (key, val) => {
    setAnswers(a => ({ ...a, [key]: val }));
    if (step < questions.length - 1) setTimeout(() => setStep(s => s + 1), 300);
    else checkEligibility({ ...answers, [key]: val });
  };

  const checkEligibility = async (ans) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/eligibility`, { nationality: nat, destination: dest, purpose: vType, ...ans });
      setResult(data);
    } catch {} finally { setLoading(false); }
  };

  const levelColors = { High: "text-emerald-600", Medium: "text-blue-600", Low: "text-red-600" };
  const levelBg = { High: "bg-emerald-50", Medium: "bg-blue-50", Low: "bg-red-50" };

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50" >
      <div className="max-w-xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Back</button>

        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4"><Shield className="w-7 h-7 text-blue-600" /></div>
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Eligibility Check</h1>
          <p className="text-sm text-slate-500">Quick assessment of your visa approval chances</p>
        </div>

        {!result ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-elevated rounded-2xl p-8">
            <div className="flex gap-2 mb-8">
              {questions.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? "bg-blue-600" : "bg-white/10"}`} />
              ))}
            </div>

            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <p className="text-lg text-slate-900 font-medium mb-6">{questions[step].label}</p>
              <div className="flex gap-4">
                <button onClick={() => handleAnswer(questions[step].key, true)} data-testid={`eligibility-yes-${step}`}
                  className={`flex-1 py-4 rounded-xl text-sm font-semibold transition-all ${answers[questions[step].key] === true ? "bg-blue-600 text-white" : "border border-slate-200 text-slate-900 hover:bg-slate-50"}`}>
                  Yes
                </button>
                <button onClick={() => handleAnswer(questions[step].key, false)} data-testid={`eligibility-no-${step}`}
                  className={`flex-1 py-4 rounded-xl text-sm font-semibold transition-all ${answers[questions[step].key] === false ? "bg-white/10 text-slate-900" : "border border-slate-200 text-slate-900 hover:bg-slate-50"}`}>
                  No
                </button>
              </div>
            </motion.div>

            {loading && <div className="text-center mt-6"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="card-elevated rounded-2xl p-8 text-center" data-testid="eligibility-result">
            <div className={`w-20 h-20 rounded-full ${levelBg[result.level]} flex items-center justify-center mx-auto mb-5`}>
              {result.level === "High" ? <CheckCircle className="w-10 h-10 text-emerald-600" /> : result.level === "Medium" ? <Shield className="w-10 h-10 text-blue-600" /> : <AlertCircle className="w-10 h-10 text-red-600" />}
            </div>
            <div className={`text-4xl font-bold heading-font mb-2 ${levelColors[result.level]}`}>{result.score}%</div>
            <p className={`text-lg font-semibold ${levelColors[result.level]} mb-1`}>{result.level} Approval Chance</p>
            <p className="text-sm text-slate-500 mb-6">Based on your profile assessment</p>

            {result.tips.length > 0 && (
              <div className="text-left bg-slate-50 rounded-xl p-5 mb-6">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Tips to improve</h4>
                <ul className="space-y-2">
                  {result.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-500"><div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 shrink-0" />{t}</li>
                  ))}
                </ul>
              </div>
            )}

            <button onClick={() => navigate(`/apply?destination=${dest}&nationality=${nat}&type=${vType}&processing=${proc}`)} data-testid="eligibility-continue"
              className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
              Continue Application <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
